/**
 * Dettaglio di un'uscita (V71).
 *
 * I MOVIMENTI A REGISTRO sono in pagina, non nascosti: un'uscita può aver
 * scalato più aree — una per ogni area da cui provenivano i big bag — e il peso
 * in testata non dice da dove sono usciti i kg. Dopo un annullamento la stessa
 * lista porta anche le compensazioni, perché il ledger è append-only.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Truck, Pencil, Trash2, ChevronLeft, CheckCircle2, Circle, Printer } from 'lucide-react';
import {
  getUscita, annullaUscita, setEcomaticoUscita, type UscitaDetail,
} from '../api/uscite';
import { listTrasportatori } from '../api/trasportatori';
import type { Trasportatore } from '../types/trasportatore';
import StatoUscitaBadge from '../components/uscite/StatoUscitaBadge';
import ModificaUscitaModal from '../components/uscite/ModificaUscitaModal';
import EliminaMagazzinoModal from '../components/magazzino/EliminaMagazzinoModal';
import { EcomaticoBadge } from '../components/magazzino/Ecomatico';
import StampaEtichettaModal from '../components/produzioni/StampaEtichettaModal';
import { getEtichettaBigBag } from '../api/magazzino';
import { useAuthStore } from '../store/authStore';
import { fmtKg } from '../lib/saldo';
import { canUscite } from '../lib/constants';
import { apiErrorMessage } from '../lib/apiError';

function fmtData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
}

function Riga({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-stone-mid/60 last:border-0">
      <dt className="text-ink-soft text-sm">{label}</dt>
      <dd className="text-ink-dark text-sm text-right">{children}</dd>
    </div>
  );
}

export default function DettaglioUscitaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [uscita, setUscita] = useState<UscitaDetail | null>(null);
  // Etichetta del big bag, aperta dalla riga: la rigenerazione non cambia ne'
  // il QR ne' la data stampata, quindi si puo' fare quando serve.
  const [etichetta, setEtichetta] = useState<{ id: string; token: string } | null>(null);
  const [trasportatori, setTrasportatori] = useState<Trasportatore[]>([]);
  const [nonTrovata, setNonTrovata] = useState(false);
  const [modifica, setModifica] = useState(false);
  const [annullando, setAnnullando] = useState(false);
  const [esito, setEsito] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const userRole = useAuthStore((s) => s.user?.role ?? '');
  const puoAgire = canUscite(userRole);

  const reload = useCallback(() => {
    void (async () => {
      try { setUscita(await getUscita(id)); }
      catch { setNonTrovata(true); }
    })();
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    void (async () => { setTrasportatori((await listTrasportatori({ limit: 100 })).items); })();
  }, []);

  if (nonTrovata) {
    return (
      <div className="p-6">
        <p className="text-sm text-ink-soft">{t('uscite.dettaglio.non_trovata')}</p>
        <button type="button" onClick={() => navigate('/uscite')} className="mt-3 text-sm text-sage-dark hover:underline">
          {t('uscite.torna_elenco')}
        </button>
      </div>
    );
  }
  if (!uscita) return <div className="p-6 text-sm text-ink-soft">{t('uscite.caricamento')}</div>;

  const fatturata = uscita.stato === 'fatturata' || uscita.stato === 'pagata';
  const annullata = uscita.stato === 'annullata';

  async function toggleEcomatico() {
    if (!uscita) return;
    setBusy(true);
    setErrore(null);
    try { setUscita(await setEcomaticoUscita(uscita.id, !uscita.registrato_ecomatico)); }
    // Senza questo ramo un fallimento lascerebbe il badge invariato e nessun
    // messaggio: l'operatore crederebbe di aver trascritto e non l'avrebbe fatto.
    catch (err: unknown) { setErrore(apiErrorMessage(err)); }
    finally { setBusy(false); }
  }

  // Gli errori NON si catturano qui: la modale li mostra al suo interno e
  // resta aperta. Chiuderla su un 409 «già fatturata» farebbe sparire il
  // motivo insieme alla finestra.
  async function confermaAnnulla() {
    if (!uscita) return;
    const res = await annullaUscita(uscita.id);
    setAnnullando(false);
    setEsito(t('uscite.annullamento.esito', {
      bigbag: res.big_bags_liberati, movimenti: res.movimenti_compensazione,
    }));
    reload();
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button type="button" onClick={() => navigate('/uscite')}
        className="text-xs text-ink-soft hover:text-sage-dark inline-flex items-center gap-1 mb-3">
        <ChevronLeft size={14} /> {t('uscite.torna_elenco')}
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Truck size={20} className="text-sage" />
          <h1 className="text-xl font-semibold text-ink-dark">{t('uscite.dettaglio.titolo', { codice: uscita.codice })}</h1>
          <StatoUscitaBadge stato={uscita.stato} />
        </div>
        {puoAgire && !annullata && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => void toggleEcomatico()} disabled={busy}
              data-testid="btn-ecomatico"
              className="px-2.5 py-1.5 text-xs text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg flex items-center gap-1 disabled:opacity-50">
              {uscita.registrato_ecomatico ? <Circle size={13} /> : <CheckCircle2 size={13} />}
              {uscita.registrato_ecomatico ? t('uscite.lista.segna_da_registrare') : t('uscite.lista.segna_registrato')}
            </button>
            <button type="button" onClick={() => setModifica(true)} data-testid="btn-modifica-uscita"
              className="px-2.5 py-1.5 text-xs text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg flex items-center gap-1">
              <Pencil size={13} /> {t('uscite.dettaglio.azione_modifica')}
            </button>
            <button type="button" onClick={() => { setErrore(null); setAnnullando(true); }}
              disabled={fatturata} data-testid="btn-annulla-uscita"
              title={fatturata ? t('uscite.dettaglio.annulla_disabilitato') : undefined}
              className="px-2.5 py-1.5 text-xs text-accent-danger border border-[#FECACA] rounded-lg hover:bg-accent-danger-bg flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
              <Trash2 size={13} /> {t('uscite.dettaglio.azione_annulla')}
            </button>
          </div>
        )}
      </div>

      {esito && (
        <div className="mb-4 px-3 py-2 bg-sage-light border border-sage/30 rounded text-sm text-sage-dark" data-testid="uscita-esito">
          {esito}
        </div>
      )}
      {errore && (
        <div className="mb-4 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid="uscita-errore">
          {errore}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <section className="bg-white border border-stone-mid rounded-xl p-4">
          <h2 className="text-sm font-semibold text-ink-dark mb-2">{t('uscite.dettaglio.dati_accompagnamento')}</h2>
          <dl>
            <Riga label={t('uscite.dettaglio.lbl_tipo')}>{t('uscite.tipo.RP')}</Riga>
            <Riga label={t('uscite.dettaglio.lbl_direzione')}>
              {uscita.tipo_operazione === 'vendita'
                ? t('uscite.direzione.vendita')
                : t('uscite.direzione.conferimento_a_terzi')}
            </Riga>
            <Riga label={t('uscite.dettaglio.lbl_rifiuto')}>
              {uscita.descrizione_rifiuto} <span className="text-ink-faint">{uscita.cer_code}</span>
            </Riga>
            <Riga label={t('uscite.dettaglio.lbl_fir')}>{uscita.numero_fir}</Riga>
            <Riga label={t('uscite.dettaglio.lbl_data_partenza')}>{fmtData(uscita.data_partenza)}</Riga>
            <Riga label={t('uscite.dettaglio.lbl_cliente')}>{uscita.customer_ragione_sociale ?? '—'}</Riga>
            <Riga label={t('uscite.dettaglio.lbl_trasportatore')}>{uscita.trasportatore_ragione_sociale ?? '—'}</Riga>
            <Riga label={t('uscite.dettaglio.lbl_peso')}>{fmtKg(uscita.peso_kg)} kg</Riga>
            <Riga label={t('uscite.dettaglio.lbl_peso_ponte')}>
              {uscita.peso_ponte_kg != null ? `${fmtKg(uscita.peso_ponte_kg)} kg` : '—'}
            </Riga>
            {/* Una riga sola per i due canali: i due id non convivono mai
                (CHECK di V71), e due righe di cui una sempre vuota direbbero
                che manca qualcosa. */}
            <Riga label={t('uscite.dettaglio.lbl_fattura')}>
              {uscita.fattura_id ? (
                <button type="button" data-testid="link-fattura"
                  onClick={() => navigate(`/fatturazione?fattura=${uscita.fattura_id}`)}
                  className="text-sage-dark hover:underline">
                  {t('uscite.lista.vedi_fattura')}
                </button>
              ) : uscita.fattura_passiva_id ? (
                <button type="button" data-testid="link-fattura-passiva"
                  onClick={() => navigate(`/fatturazione?sezione=passive&passiva=${uscita.fattura_passiva_id}`)}
                  className="text-sage-dark hover:underline">
                  {t('uscite.lista.vedi_fattura_passiva')}
                </button>
              ) : '—'}
            </Riga>
            <Riga label={t('uscite.dettaglio.lbl_note')}>{uscita.note || '—'}</Riga>
            <Riga label={t('uscite.dettaglio.lbl_registrata_da')}>{uscita.creato_da_nome ?? '—'}</Riga>
            <Riga label={t('uscite.dettaglio.lbl_registrata_il')}>
              {new Date(uscita.created_at).toLocaleString('it-IT')}
            </Riga>
            <Riga label={t('uscite.lista.col_ecomatico')}>
              <EcomaticoBadge registrato={uscita.registrato_ecomatico} at={uscita.registrato_ecomatico_at}
                daNome={uscita.registrato_ecomatico_da_nome} />
            </Riga>
          </dl>
        </section>

        <section className="bg-white border border-stone-mid rounded-xl p-4">
          <h2 className="text-sm font-semibold text-ink-dark mb-2">
            {t('uscite.dettaglio.bigbag_titolo', { n: uscita.big_bags.length })}
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                <th className="py-1.5">{t('uscite.dettaglio.bigbag_col_token')}</th>
                <th className="py-1.5">{t('uscite.dettaglio.bigbag_col_tipologia')}</th>
                <th className="py-1.5">{t('uscite.dettaglio.bigbag_col_area')}</th>
                <th className="py-1.5 text-right">{t('uscite.dettaglio.bigbag_col_peso')}</th>
                <th className="py-1.5">{t('uscite.dettaglio.bigbag_col_data')}</th>
              </tr>
            </thead>
            <tbody>
              {uscita.big_bags.map((b) => (
                <tr key={b.id} className="border-t border-stone-mid/60" data-testid={`dettaglio-bigbag-${b.qr_token}`}>
                  <td className="py-1.5 flex items-center gap-2">
                    <button type="button" onClick={() => navigate(`/big-bags/${b.qr_token}`)}
                      className="font-mono text-xs text-sage-dark hover:underline">
                      {b.qr_token.slice(0, 8)}
                    </button>
                    {/* L'etichetta si rigenera da ovunque si veda un big bag:
                        il QR e la data stampata non cambiano, e' la stessa
                        unita' fisica. */}
                    <button type="button" data-testid={`dettaglio-bigbag-etichetta-${b.qr_token}`}
                      title={t('uscite.dettaglio.bigbag_etichetta')}
                      aria-label={t('uscite.dettaglio.bigbag_etichetta')}
                      onClick={() => setEtichetta({ id: b.id, token: b.qr_token })}
                      className="text-ink-soft hover:text-sage-dark">
                      <Printer size={13} />
                    </button>
                  </td>
                  <td className="py-1.5">{b.tipologia_nome ?? '—'}</td>
                  <td className="py-1.5">{b.area_codice ?? '—'}</td>
                  <td className="py-1.5 text-right">{fmtKg(b.peso_kg)}</td>
                  <td className="py-1.5 text-ink-mid">{fmtData(b.data_produzione)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <section className="bg-white border border-stone-mid rounded-xl p-4 mt-5">
        <h2 className="text-sm font-semibold text-ink-dark">{t('uscite.dettaglio.movimenti_titolo')}</h2>
        <p className="text-xs text-ink-soft mb-2">{t('uscite.dettaglio.movimenti_nota')}</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              <th className="py-1.5">{t('uscite.dettaglio.mov_col_area')}</th>
              <th className="py-1.5">{t('uscite.dettaglio.mov_col_materiale')}</th>
              <th className="py-1.5 text-right">{t('uscite.dettaglio.mov_col_delta')}</th>
              <th className="py-1.5">{t('uscite.dettaglio.mov_col_causale')}</th>
              <th className="py-1.5">{t('uscite.dettaglio.mov_col_data')}</th>
            </tr>
          </thead>
          <tbody>
            {uscita.movimenti.map((m, i) => (
              <tr key={`${m.area_id}-${m.causale}-${i}`} className="border-t border-stone-mid/60"
                data-testid={`uscita-movimento-${i}`}>
                <td className="py-1.5">{m.area_codice ?? '—'}</td>
                <td className="py-1.5">{m.materiale}</td>
                <td className={`py-1.5 text-right tabular-nums ${m.delta_kg < 0 ? 'text-accent-danger' : 'text-sage-dark'}`}>
                  {m.delta_kg > 0 ? '+' : ''}{fmtKg(m.delta_kg)}
                </td>
                <td className="py-1.5 text-ink-mid font-mono text-xs">{m.causale}</td>
                <td className="py-1.5 text-ink-mid">{new Date(m.created_at).toLocaleString('it-IT')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {modifica && (
        <ModificaUscitaModal
          uscita={uscita}
          trasportatori={trasportatori}
          onClose={() => setModifica(false)}
          onSaved={(u) => { setUscita(u); setModifica(false); }}
        />
      )}

      {annullando && (
        <EliminaMagazzinoModal
          title={t('uscite.annullamento.titolo', { codice: uscita.codice })}
          righe={[
            t('uscite.annullamento.riga_kg', { kg: fmtKg(uscita.peso_kg) }),
            t('uscite.annullamento.riga_bigbag', { n: uscita.big_bags.length }),
          ]}
          avvertenza={t('uscite.annullamento.riga_fir', { fir: uscita.numero_fir })}
          confermaLabel={t('uscite.annullamento.conferma')}
          onConfirm={() => confermaAnnulla()}
          onClose={() => setAnnullando(false)}
          testid="annulla-uscita-modal"
        />
      )}
      {etichetta && (
        <StampaEtichettaModal
          title={t('uscite.dettaglio.bigbag_etichetta')}
          filename={`etichetta-${etichetta.token.slice(0, 8)}.pdf`}
          fetchPdf={() => getEtichettaBigBag(etichetta.id)}
          onClose={() => setEtichetta(null)}
        />
      )}
    </div>
  );
}
